import { useCallback, RefObject } from 'react'
import { FileExplorerItem } from '@features/file-explorer/types'
import { useSelectionStore } from '@stores/useSelectionStore'
import { FileExplorerModalsHandle } from '@features/file-explorer/components/FileExplorerModals'

interface UseFileExplorerHandlersParams {
  items: FileExplorerItem[]
  currentFolderId: string | null
  modalRef: RefObject<FileExplorerModalsHandle | null>
  onItemClick: (item: FileExplorerItem) => void
  showToast: (type: 'info' | 'success' | 'error' | 'loading', title: string, options?: any) => string
  t: (key: string, options?: any) => string
}

export function useFileExplorerHandlers({
  items,
  currentFolderId,
  modalRef,
  onItemClick,
  showToast,
  t
}: UseFileExplorerHandlersParams) {
  const selectItem = useSelectionStore(state => state.toggle)
  const selectRange = useSelectionStore(state => state.selectRange)
  const selectAll = useSelectionStore(state => state.selectAll)
  const clearSelection = useSelectionStore(state => state.clear)

  const getItemKey = (item: FileExplorerItem): string => {
    return `${item.is_folder ? 'folder' : 'document'}-${item.id}`
  }

  const handleSelectItem = useCallback((item: FileExplorerItem, index: number, shiftKey: boolean) => {
    const itemKey = getItemKey(item)
    // Read lastSelectedIndex directly from store to avoid stale closure
    const lastSelectedIndex = useSelectionStore.getState().lastSelectedIndex

    if (shiftKey && lastSelectedIndex !== null) {
      const start = Math.min(lastSelectedIndex, index)
      const end = Math.max(lastSelectedIndex, index)
      const rangeKeys = items.slice(start, end + 1).map(item => getItemKey(item))
      selectRange(rangeKeys, lastSelectedIndex)
    } else {
      selectItem(itemKey, index)
    }
  }, [items, selectItem, selectRange])

  const handleSelectAll = useCallback(() => {
    const allKeys = items.map(item => getItemKey(item))
    selectAll(allKeys)
  }, [items, selectAll])

  const handleClearSelection = useCallback(() => {
    clearSelection()
  }, [clearSelection])

  const handleDeleteClick = useCallback((item: FileExplorerItem) => {
    modalRef.current?.openDeleteModal(item)
  }, [modalRef])

  const handleBulkDelete = useCallback(() => {
    const selectedKeys = useSelectionStore.getState().getSelectedKeys()
    const selectedItemObjects = items.filter(item =>
      selectedKeys.includes(getItemKey(item))
    )
    modalRef.current?.openBulkDeleteModal(selectedItemObjects)
  }, [items, modalRef])

  const handleRenameClick = useCallback((item: FileExplorerItem) => {
    modalRef.current?.openRenameModal(item)
  }, [modalRef])

  const handleDownload = useCallback((item: FileExplorerItem) => {
    const downloadUrl = item.is_folder
      ? `/folders/${item.id}/download`
      : `/documents/${item.id}/download`

    window.location.href = downloadUrl
  }, [])

  const handleBulkDownload = useCallback(() => {
    const selectedKeys = useSelectionStore.getState().getSelectedKeys()
    const selectedItemObjects = items.filter(item =>
      selectedKeys.includes(getItemKey(item))
    )

    if (selectedItemObjects.length === 0) return

    showToast('info', t('fileExplorer.bulkActions.download'), {
      message: t('fileExplorer.bulkActions.downloadingMultiple', { count: selectedItemObjects.length }),
      duration: 2000
    })

    const itemsPayload = selectedItemObjects.map(item => ({
      type: item.is_folder ? 'Folder' : 'Document',
      id: item.id
    }))

    const form = document.createElement('form')
    form.method = 'POST'
    form.action = '/bulk_download'

    const csrfToken = document.querySelector('meta[name="csrf-token"]')?.getAttribute('content')
    if (csrfToken) {
      const csrfInput = document.createElement('input')
      csrfInput.type = 'hidden'
      csrfInput.name = 'authenticity_token'
      csrfInput.value = csrfToken
      form.appendChild(csrfInput)
    }

    const itemsInput = document.createElement('input')
    itemsInput.type = 'hidden'
    itemsInput.name = 'items'
    itemsInput.value = JSON.stringify(itemsPayload)
    form.appendChild(itemsInput)

    if (currentFolderId) {
      const folderInput = document.createElement('input')
      folderInput.type = 'hidden'
      folderInput.name = 'current_folder_id'
      folderInput.value = currentFolderId
      form.appendChild(folderInput)
    }

    document.body.appendChild(form)
    form.submit()
    document.body.removeChild(form)

    setTimeout(() => {
      handleClearSelection()
    }, 500)
  }, [items, showToast, t, handleClearSelection, currentFolderId])

  const handleShow = useCallback((item: FileExplorerItem) => {
    onItemClick(item)
  }, [onItemClick])

  const handleManagePermissions = useCallback((item: FileExplorerItem) => {
    modalRef.current?.openPermissionsModal(item)
  }, [modalRef])

  return {
    getItemKey,
    handleSelectItem,
    handleSelectAll,
    handleClearSelection,
    handleDeleteClick,
    handleBulkDelete,
    handleRenameClick,
    handleDownload,
    handleBulkDownload,
    handleShow,
    handleManagePermissions
  }
}
